"use client";

import { IconArrowUp, IconList } from "@tabler/icons-react";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import Accordion from "src/components/common/Accordion/Accordion";
import Button from "src/components/common/Button/Button";
import IconTile from "src/components/common/IconTile/IconTile";
import Label from "src/components/form/Label/Label";
import { usePathname, useSearchParams } from "src/lib/Navigation";
import { useExtracted } from "#i18n";
import styles from "./DocsToc.module.scss";

export interface DocsTocProps {
  className?: string;
  compact?: boolean;
  title?: string;
  /** Headings known before mount (from the page source), so the table renders on the server. */
  headings?: Array<{ id: string; value: string; depth: number }>;
  backToTop?: boolean;
  extraContent?: React.ReactNode;
}

export interface Heading {
  id: string;
  value: string;
  depth: number;
}

const DocsToc = (props: DocsTocProps) => {
  const translateMessage = useExtracted();
  const { className, title, backToTop = true, extraContent, compact = false, headings } = props;
  const [activeSlug, setActiveSlug] = useState<string>("");
  const [toc, setToc] = useState<Array<Heading>>(headings ?? []);
  const tocRef = useRef<HTMLUListElement>(null);
  let embedded = false;
  const searchParams = useSearchParams();
  if (searchParams.get("embedded") === "true") {
    embedded = true;
  }

  const pathname = usePathname();

  useEffect(() => {
    const root = document.getElementById("main-content");
    if (!root) return;
    let signature = "";
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) if (entry.isIntersecting) setActiveSlug(entry.target.id);
      },
      { rootMargin: "-80px 0px -80% 0px", threshold: 1 },
    );
    const updateHeadings = () => {
      const headings = Array.from(root.querySelectorAll("article :is(h2,h3,h4,h5,h6)[id]")).filter(
        (heading) =>
          heading.id.trim() && (pathname !== "/docs/changelog" || heading.tagName === "H2"),
      );
      const items = headings.map((heading) => ({
        id: heading.id,
        value: heading.textContent || "",
        depth: Number.parseInt(heading.tagName.slice(1), 10),
      }));
      const nextSignature = JSON.stringify(items);
      if (nextSignature === signature) return;
      signature = nextSignature;
      setToc(items);
      setActiveSlug("");
      observer.disconnect();
      for (const heading of headings) observer.observe(heading);
    };
    updateHeadings();
    const mutations = new MutationObserver(updateHeadings);
    mutations.observe(root, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ["id"],
    });
    return () => {
      mutations.disconnect();
      observer.disconnect();
    };
  }, [pathname]);

  const scrollToTop = () => {
    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    window.scrollTo({ top: 0, behavior: reduceMotion ? "auto" : "smooth" });
    document.getElementById("main-content")?.focus({ preventScroll: true });
  };

  const hasHeadings = toc.length > 0;
  const activeIndex = toc.findIndex(({ id }) => id === activeSlug);
  const showBackToTop = backToTop && activeIndex >= 2;
  const hasMetaInfo = showBackToTop || extraContent;

  if (embedded || !hasHeadings) {
    return null;
  }

  const links = (
    <ul ref={tocRef} className={clsx(styles.list, "scrollbar-thin")}>
      {toc.map(({ id, value, depth }) => (
        <li className={styles.item} key={id}>
          <a
            onClick={() => setActiveSlug(id)}
            href={`#${id}`}
            className={clsx(
              {
                3: styles.depth3,
                4: styles.depth4,
                5: styles.depth5,
                6: styles.depth6,
              }[depth],
              styles.link,
            )}
            aria-current={id === activeSlug ? "location" : undefined}
            data-active={id === activeSlug ? "true" : undefined}
          >
            {value}
          </a>
        </li>
      ))}
    </ul>
  );

  if (compact)
    return (
      <Accordion className={clsx(styles.root, styles.compact, className)}>
        <Accordion.Item value="contents">
          <Accordion.Trigger className={styles.compactTrigger}>
            {title ?? translateMessage("On this page")}
            <Accordion.Indicator />
          </Accordion.Trigger>
          <Accordion.Content>
            <nav aria-label={translateMessage("Table of contents")}>{links}</nav>
            {extraContent && <div className={styles.footer}>{extraContent}</div>}
          </Accordion.Content>
        </Accordion.Item>
      </Accordion>
    );

  return (
    <nav
      className={clsx(styles.root, className)}
      aria-label={translateMessage("Table of contents")}
    >
      {hasHeadings && (
        <>
          <div className={styles.header}>
            <IconTile size="sm" color="primary" fill="soft">
              <IconList size={16} stroke={1.8} />
            </IconTile>
            <Label as="h2" className={styles.title}>
              {title ?? translateMessage("On this page")}
            </Label>
          </div>
          {links}
        </>
      )}

      {hasMetaInfo && (
        <div className={styles.footer}>
          {extraContent}

          {showBackToTop && (
            <Button
              label={translateMessage("Back to top")}
              variant="bare"
              size="sm"
              ripple={false}
              className={clsx(styles.backToTop, "!w-full !justify-start")}
              onClick={scrollToTop}
            >
              <IconArrowUp aria-hidden size={15} stroke={1.8} />
              {translateMessage("Back to top")}
            </Button>
          )}
        </div>
      )}
    </nav>
  );
};

export default DocsToc;
